import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BasicTable,
  CardComponent,
  OverviewCard,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { observer } from 'mobx-react';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { paymentIntegrityConstants } from '../../core-utils/messages/paymentIntegrityConstants';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { numFormatterWithSuffix } from '../../core-utils/helper-utils/helper';
import {
  formatCategories,
  getPaymentIntegrityCategoryColumns,
  getSummaryData,
} from '../../core-utils/helper-utils/paymentIntegrityHelper';
import {
  getCategories,
  getSummary,
} from '../../core-utils/axios/payment-integrity/paymentIntegrity';
import { useStores } from '../../stores';
import Snackbar from '../../_components/atoms/Snackbar';
import Loader from '../../_components/molecules/Loader';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import { CategoryData, SummaryData } from './PaymentIntegrity.types';
import { useNavigate } from 'react-router-dom';
import { PAYMENT_INTEGRITY_INTERNAL } from '../../core-utils/routes';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { getPlanDate } from '../../_components/molecules/QuaterlyReportModal';
import { PlanDates } from '../../core-utils/custom-hooks/PlanDates';
import { getCohortValue } from '../../core-utils/helper-utils/overviewCardHelper';

export const getTitleTypography = (label: string) => {
  return (
    <Typography
      fontSize="18px"
      lineHeight="100%"
      textColor={patientAccessTheme.palette.text.highemphasis}
      fontType="semiBold"
    >
      {label}
    </Typography>
  );
};

export const getSubTextTypography = (label: string) => {
  return (
    <Typography
      fontSize="17px"
      lineHeight="100%"
      textColor={patientAccessTheme.palette.text.mediumemphasis}
      fontType="semiBold"
    >
      {label}
    </Typography>
  );
};

export const getDateSelection = (
  value: string,
  options: any[],

  onChange: (value: any) => void,
  className?: string,
) => (
  <Grid className={className}>
    <Dropdown
      label=""
      dropDownOptions={options}
      dropDownValues={options}
      value={value}
      width="300px"
      selectStyles={{ width: '300px' }}
      handleChange={onChange}
    />
  </Grid>
);

const PaymentIntegrityPage = observer(() => {
  const { classes } = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '1182px',
      padding: '40px 0px 66px 63px',
    },
    hr: {
      width: '100%',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      margin: '18px 0px',
    },
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    overviewCardContainer: {
      display: 'flex',
      gap: '1rem',
    },
    flex: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
  })();

  const overviewCardStyles = {
    rootStyles: {
      width: '200px',
      height: '90px',
    },
  };

  const messages = paymentIntegrityConstants;
  const employerGroupStore = useStores().employerGroupStore;
  const quaterlyReportStore = useStores().quaterlyReportStore;
  const { snackbarState, handleCloseSnackBar, openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const egNid = employerGroupStore.employerGroupResponseData.eg_nid[0];
  const dropDownOptionsPlanPeriod: string[] = quaterlyReportStore.planDates.map(
    (plan) => getPlanDate(plan),
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [overallSummaryData, setOverallSummaryData] = useState<SummaryData>({
    noOfClaims: 0,
    totalPaid: 0,
  });
  const [summaryData, setSummaryData] = useState<SummaryData>({
    noOfClaims: 0,
    totalPaid: 0,
  });

  const [categories, setCategories] = useState<CategoryData[]>([]);
  const currentPlan: PlanDates = useMemo(() => {
    return quaterlyReportStore.planDates?.[
      quaterlyReportStore.selectedPlanIndex
    ];
  }, [quaterlyReportStore.planDates, quaterlyReportStore.selectedPlanIndex]);

  const planPeriod = useMemo(() => {
    return currentPlan ? getPlanDate(currentPlan) : '';
  }, [currentPlan]);

  const handleHistory = (category: string) => {
    navigate(PAYMENT_INTEGRITY_INTERNAL, {
      state: {
        category,
        categoryList: categories.map((category) => category.category),
        summaryData: overallSummaryData,
        currentPlan: {
          incurred_start_date: currentPlan.incurred_start_date,
          incurred_end_date: currentPlan.incurred_end_date,
          paid_start_date: currentPlan.paid_start_date,
          paid_end_date: currentPlan.paid_end_date,
        },
      },
    });
  };
  const handlePlanPeriodChange = (event: ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    const planIndex = dropDownOptionsPlanPeriod.findIndex(
      (plan) => plan === value,
    );

    quaterlyReportStore.setSelectedPlanIndex(planIndex);
  };

  const OverviewCards = () => {
    return (
      <Grid className={classes.overviewCardContainer}>
        <OverviewCard
          title={messages.CARDS.CLAIMS}
          value={getCohortValue(
            numFormatterWithSuffix(summaryData?.noOfClaims),
            numFormatterWithSuffix(overallSummaryData?.noOfClaims),
          )}
          rootStyles={overviewCardStyles.rootStyles}
        />
        <OverviewCard
          title={messages.CARDS.TOTAL_PAID}
          value={getCohortValue(
            `$${numFormatterWithSuffix(summaryData?.totalPaid)}`,
            `$${numFormatterWithSuffix(overallSummaryData?.totalPaid)}`,
          )}
          rootStyles={overviewCardStyles.rootStyles}
        />
      </Grid>
    );
  };

  const TableCard = () => {
    return (
      <CardComponent
        title={messages.TABLE}
        titleSubTitleMargin="1rem"
        body={
          <BasicTable
            columns={getPaymentIntegrityCategoryColumns()}
            rowsData={formatCategories(categories, handleHistory)}
            displayType="box"
            isFullTable
            headerCellHeight="46px"
            headerColor={patientAccessTheme.palette.grey[700]}
            headerTextColor={patientAccessTheme.palette.status.white}
            cellHeight="46px"
            cellPadding="12px 9px 12px 14px"
          />
        }
      />
    );
  };

  const apiCalls = async () => {
    if (!currentPlan || !egNid) {
      return;
    }

    setLoading(true);
    try {
      const responses = await Promise.allSettled([
        getSummary(
          egNid,
          currentPlan.incurred_start_date,
          currentPlan.incurred_end_date,
          currentPlan.paid_start_date,
          currentPlan.paid_end_date,
        ),
        getCategories(
          egNid,
          currentPlan.paid_start_date,
          currentPlan.paid_end_date,
        ),
      ]);

      if (responses[0].status === 'fulfilled') {
        setOverallSummaryData(responses[0].value.data?.data);
      }
      if (responses[1].status === 'fulfilled') {
        setCategories(responses[1].value.data?.data);
        setSummaryData(getSummaryData(responses[1].value.data?.data));
      }
    } catch {
      openSnackbar(messages.ERROR, 'error', 2000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!egNid || !currentPlan) {
      return;
    }
    apiCalls();
  }, [egNid, currentPlan]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Grid className={classes.root}>
          <PageTitleAndSubTitle
            title={
              <Grid className={classes.flex}>
                {getTitleTypography(messages.TITLE)}

                {
                  <>
                    {getSubTextTypography('for')}
                    {getDateSelection(
                      planPeriod,
                      dropDownOptionsPlanPeriod,
                      handlePlanPeriodChange,
                      classes.flex,
                    )}
                  </>
                }
              </Grid>
            }
            subTitle={messages.SUBTITLE}
            marginTopSubTitle="1rem"
          />
          <hr className={classes.hr} />
          <Grid className={classes.mainContainer}>
            <OverviewCards />
            <TableCard />
          </Grid>
        </Grid>
      )}

      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </>
  );
});

export default PaymentIntegrityPage;
